import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import {
  AddFeedEventIcon,
  AddFeedPurchaseIcon,
  SwardCompositionIcon,
} from "common/icons";
import FertilizerEventIcon from "common/icons/FertilizerEventIcon";
import FieldPreparationEventIcon from "common/icons/FieldPreparationEventIcon";
import YieldEventIcon from "common/icons/YieldEventIcon";
import { useState } from "react";

export type EventType =
  | "reseed"
  | "feed"
  | "feedPurchase"
  | "yield"
  | "fertilizer"
  | "fieldPreparation";

interface EventFilterButtonProps {
  selectedTypes: EventType[];
  onFilterChange: (selectedTypes: EventType[]) => void;
}

const ALL_EVENT_TYPES: Array<{
  type: EventType;
  label: string;
  icon: JSX.Element;
}> = [
  { type: "reseed", label: "Re-seeding", icon: <SwardCompositionIcon /> },
  { type: "feed", label: "Feed", icon: <AddFeedEventIcon /> },
  {
    type: "feedPurchase",
    label: "Feed Purchase",
    icon: <AddFeedPurchaseIcon />,
  },
  { type: "yield", label: "Yield", icon: <YieldEventIcon /> },
  { type: "fertilizer", label: "Fertilizer", icon: <FertilizerEventIcon /> },
  {
    type: "fieldPreparation",
    label: "Field Preparation",
    icon: <FieldPreparationEventIcon />,
  },
];

export default function EventFilterButton({
  selectedTypes,
  onFilterChange,
}: EventFilterButtonProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleTypeToggle = (type: EventType) => {
    const newSelectedTypes = selectedTypes.includes(type)
      ? selectedTypes.filter((t) => t !== type)
      : [...selectedTypes, type];
    onFilterChange(newSelectedTypes);
  };

  const handleSelectAll = () => {
    // When selecting all, clear the filter (show all events)
    onFilterChange([]);
  };

  const handleClearAll = () => {
    // Clear filter means show all events
    onFilterChange([]);
  };

  const isAllSelected =
    selectedTypes.length === ALL_EVENT_TYPES.length ||
    selectedTypes.length === 0;
  const isFilterActive =
    selectedTypes.length > 0 && selectedTypes.length < ALL_EVENT_TYPES.length;

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<FilterListIcon sx={{ width: 16, height: 16 }} />}
        onClick={handleMenuOpen}
        sx={{
          height: "32px",
          fontSize: "14px",
          fontWeight: 500,
          textTransform: "none",
          backgroundColor: "#ffffff",
          borderColor: "#e4e4e7",
          color: "#18181b",
          borderRadius: "6px",
          padding: "8px 12px",
          gap: "8px",
          fontFamily: "Inter, sans-serif",
          "&:hover": {
            backgroundColor: "#fafafa",
            borderColor: "#e4e4e7",
          },
          ...(isFilterActive && {
            backgroundColor: "#f4f4f5",
            borderColor: "#e4e4e7",
          }),
        }}
      >
        Filter
        {isFilterActive && (
          <Box
            sx={{
              ml: 0.5,
              px: 0.75,
              py: 0.25,
              backgroundColor: "#71717a",
              color: "#ffffff",
              borderRadius: "12px",
              fontSize: "12px",
              fontWeight: 500,
              minWidth: "20px",
              textAlign: "center",
            }}
          >
            {selectedTypes.length}
          </Box>
        )}
      </Button>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            backgroundColor: "#ffffff",
            border: "1px solid #e4e4e7",
            borderRadius: "6px",
            minWidth: "220px",
            mt: 1,
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 0.75 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "24px",
                color: "#09090b",
              }}
            >
              Filter by type
            </Typography>
            {isFilterActive && (
              <Button
                onClick={handleClearAll}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  py: 0.5,
                  fontSize: "12px",
                  fontWeight: 500,
                  color: "#71717a",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "transparent",
                    color: "#09090b",
                  },
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem
          onClick={handleSelectAll}
          sx={{
            px: 1.5,
            py: 0.75,
            gap: 1,
            fontSize: "14px",
            lineHeight: "20px",
            color: "#09090b",
          }}
        >
          <Checkbox checked={isAllSelected} size="small" />
          <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
            Select all
          </Typography>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        {ALL_EVENT_TYPES.map((eventType) => {
          const isSelected = selectedTypes.includes(eventType.type);
          return (
            <MenuItem
              key={eventType.type}
              onClick={() => handleTypeToggle(eventType.type)}
              sx={{
                px: 1.5,
                py: 0.75,
                gap: 1,
                fontSize: "16px",
                lineHeight: "24px",
                color: "#09090b",
              }}
            >
              <Checkbox checked={isSelected} size="small" />
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: 16,
                    height: 16,
                  }}
                >
                  {eventType.icon}
                </Box>
                <Typography sx={{ fontSize: "16px" }}>
                  {eventType.label}
                </Typography>
              </Box>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
